import React, { Component } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Form from './Form';
import Review from './Review';
import Confirmation from './Confirmation';
import GenericError from '../../Errors/GenericError';
import NotEligible from '../../Errors/NotEligible';
import TrackingRoute from '../../TrackingRoute';
import AccountInfoPushdown from '../../AccountInfoPushdown';

import { postSwapOrder } from '../../../actions/swapEligibility';
import { sendPageloadEvent } from '../../../actions/adobe';
import { setError, setSubmit } from '../../../actions/userMessages';

import { requiresCallback } from '../../../helpers/swaps';
import { scrollToInvalid } from '../../../helpers/errors';

import {
  SWAPS,
  SWAPS_NEW,
  SWAPS_NEW_REVIEW,
  SWAPS_NEW_CONFIRMATION,
  SWAPS_NEW_ERROR,
  SWAPS_NEW_INELIGIBLE,
  withQuery,
} from '../../../helpers/routes';
import LoadingWrapper from '../../../components/LoadingWrapper';

class Swaps extends Component {
  constructor() {
    super();

    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onReviewSubmit = this.onReviewSubmit.bind(this);

    this.state = { devices: [] };
  }

  componentDidUpdate() {
    const {
      error,
      isNotEligible,
      handleSendPageloadEvent,
      match,
      loading,
    } = this.props;

    // don't send event if we're loading still
    if (loading) {
      return;
    }

    if (error) {
      handleSendPageloadEvent({
        ...match,
        override: SWAPS_NEW_ERROR,
      }, {
        deleteComponent: true,
        component: [{
          placementName: 'myaccount|devices|swaps',
          componentName: 'error',
        }],
      });
      return;
    }

    if (isNotEligible) {
      handleSendPageloadEvent({
        ...match,
        override: SWAPS_NEW_INELIGIBLE,
      }, {
        deleteComponent: true,
        component: [{
          placementName: 'myaccount|devices|swaps',
          componentName: 'ineligible',
        }],
      });
    }
  }

  onFormSubmit(devices) {
    const {
      sikEligible,
      history,
    } = this.props;

    if (requiresCallback(devices, sikEligible)) {
      // Send the customer to schedule a callback for install.
      const intent = devices.some(device => device.lob === 'video')
        ? 'DEVICE_SWAP_VIDEO_COMBO'
        : 'DEVICE_SWAP_INTERNET';
      window.location = `https://www.xfinity.com/support/schedule-callback?selectedIntent=${intent}`;
      return;
    }

    this.setState({ devices });
    history.push(withQuery(SWAPS_NEW_REVIEW));
  }

  onReviewSubmit() {
    const {
      history,
      handlePostSwapOrder,
      handleSetSubmit,
      handleSetError,
    } = this.props;

    const {
      devices,
    } = this.state;

    const payload = {
      programNames: [...devices.reduce((programs, device) => {
        device.programs.forEach((program) => {
          programs.add(program.name);
        });
        return programs;
      }, new Set())],
    };

    handleSetSubmit();

    return handlePostSwapOrder(payload)
      .then(() => (
        history.push(withQuery(SWAPS_NEW_CONFIRMATION))
      )).catch(() => {
        scrollToInvalid();

        // unknown errors trigger modal
        return handleSetError({ showModal: true });
      });
  }

  render() {
    const {
      loading,
      error,
      location: { pathname } = {},
      account,
      macaroon,
      accounts,
      isNotEligible,
    } = this.props;

    const {
      devices,
    } = this.state;

    if (devices.length === 0) {
      if (pathname === SWAPS_NEW_REVIEW) {
        return (
          <Redirect to={withQuery(SWAPS_NEW)} />
        );
      }

      if (pathname === SWAPS_NEW_CONFIRMATION) {
        return (
          <Redirect to={withQuery(SWAPS)} />
        );
      }
    }

    if (loading) {
      return <LoadingWrapper />;
    }

    if (isNotEligible) {
      return <NotEligible />;
    }

    if (error) {
      return <GenericError />;
    }

    const accountNumber = account && account.accountNumber;
    const serviceAddress = (account && account.serviceAddress) || {};
    const hasLinkedAccounts = accounts && accounts.length > 1;

    return (
      <>
        <AccountInfoPushdown
          accountNumber={accountNumber}
          hasLinkedAccounts={hasLinkedAccounts}
          serviceAddress={serviceAddress}
        />
        <Switch>
          <TrackingRoute
            exact
            path={SWAPS_NEW_REVIEW}
            render={matchProps => (
              <Review onReviewSubmit={this.onReviewSubmit} devices={devices} {...matchProps} />
            )}
          />
          <TrackingRoute
            exact
            path={SWAPS_NEW_CONFIRMATION}
            render={matchProps => (
              <Confirmation
                devices={devices}
                account={account}
                macaroon={macaroon}
                {...matchProps}
              />
            )}
          />
          <Route
            render={matchProps => <Form onFormSubmit={this.onFormSubmit} {...matchProps} />}
          />
        </Switch>
      </>
    );
  }
}

const mapStateToProps = ({
  swapEligibility: {
    loading,
    error,
    isNotEligible,
    data: { sikEligible },
  },
  account: { data: accountData },
  auth: {
    macaroon,
    macaroon: { accounts = [] } = {},
  },
}) => ({
  loading,
  error,
  account: accountData,
  macaroon,
  accounts,
  isNotEligible,
  sikEligible,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  handlePostSwapOrder: postSwapOrder,
  handleSetError: setError,
  handleSetSubmit: setSubmit,
  handleSendPageloadEvent: sendPageloadEvent,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Swaps);
